import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Skeleton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  LocalHospital as HospitalIcon,
  Pets as PetsIcon,
  Settings as SettingsIcon,
  MeetingRoom as StallIcon,
  Grass as FieldIcon,
  SwapHoriz as MoveIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import Autocomplete from '@mui/material/Autocomplete';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import api, { horseApi, Horse, treatmentSheetApi } from '../../utils/api';
import { patientLabel } from '../../utils/patientLabel';
import { formatAge } from '../../utils/dateUtils';
import { useRBAC } from '../../auth/useRBAC';
import { treatmentSheetControls } from '../../auth/pageControls';
import type {
  StallOccupancy,
  TreatmentSheet,
  LocationType,
  CreateSheetPayload,
  Stall,
} from '../../types/treatmentSheet';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computeAge(dateOfBirth?: string): string {
  return formatAge(dateOfBirth, 'yo');
}

const STATUS_COLOR_MAP: Record<string, string> = {
  green: '#4caf50',
  amber: '#ff9800',
  red: '#f44336',
  empty: '#9e9e9e',
};

// Singular labels for a managed location, keyed by its type.
const LOCATION_TYPE_LABEL: Record<LocationType, string> = {
  STALL: 'Stall',
  FIELD: 'Field',
  OTHER: 'Location',
};

// ─── StallCard ────────────────────────────────────────────────────────────────

interface StallCardProps {
  occupancy: StallOccupancy;
  // Both OPTIONAL: the board passes `undefined` for a person without manage:medical_records, and
  // the card renders no action for a handler it was not given (INV-108) — the same shape `onMove`
  // already used, so there is no second code path for "refused".
  onAdmit?: (stall: Stall) => void;
  onMove?: (occupancy: StallOccupancy) => void;
}

const StallCard: React.FC<StallCardProps> = ({ occupancy, onAdmit, onMove }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { stall, currentSheet, horse, statusColor } = occupancy;
  const isEmpty = !currentSheet || !horse;

  const handleClick = () => {
    if (!isEmpty && currentSheet) {
      navigate(`/hospital/treatment-sheets/${currentSheet.id}`);
    }
  };

  if (isEmpty) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          height: 160,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          borderStyle: 'dashed',
          borderColor: theme.palette.grey[300],
          backgroundColor: theme.palette.grey[50],
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
          {stall.name}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Empty
        </Typography>
        {onAdmit && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => onAdmit(stall)}
            sx={{ mt: 0.5 }}
          >
            Admit Patient
          </Button>
        )}
      </Paper>
    );
  }

  return (
    <Paper
      elevation={2}
      onClick={handleClick}
      sx={{
        p: 2,
        height: 160,
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        position: 'relative',
        borderLeft: `4px solid ${STATUS_COLOR_MAP[statusColor] ?? theme.palette.grey[400]}`,
        transition: 'box-shadow 0.15s',
        '&:hover': { boxShadow: theme.shadows[6] },
      }}
    >
      {/* Stall name badge */}
      <Box sx={{ position: 'absolute', top: 8, right: 10 }}>
        <Chip
          label={stall.name}
          size="small"
          sx={{ fontSize: 10, height: 20 }}
          variant="outlined"
        />
      </Box>

      {/* Status dot */}
      <Tooltip title={`Status: ${statusColor}`}>
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: STATUS_COLOR_MAP[statusColor] ?? theme.palette.grey[400],
          }}
        />
      </Tooltip>

      <Box sx={{ mt: 1.5 }}>
        <Typography variant="h6" fontWeight={700} noWrap>
          {horse?.name ?? '—'}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {horse?.breed ?? ''}{horse?.dateOfBirth ? ` · ${computeAge(horse.dateOfBirth)}` : ''}
        </Typography>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          Vet: {currentSheet?.attendingVet?.name ?? '—'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Admitted:{' '}
            {currentSheet?.admitDate
              ? format(parseISO(currentSheet.admitDate), 'MMM d, yyyy')
              : '—'}
          </Typography>
          {onMove && (
            <Tooltip title="Move to another stall">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onMove(occupancy); }}
                sx={{ ml: 0.5, color: 'text.secondary' }}
              >
                <MoveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

// ─── Admit Dialog ─────────────────────────────────────────────────────────────

interface AdmitDialogProps {
  open: boolean;
  preselectedStall?: Stall | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface UserOption {
  id: string;
  name: string;
}

const AdmitDialog: React.FC<AdmitDialogProps> = ({
  open,
  preselectedStall,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [selectedHorse, setSelectedHorse] = useState<Horse | null>(null);
  const [selectedVet, setSelectedVet] = useState<UserOption | null>(null);
  const [selectedStall, setSelectedStall] = useState<Stall | null>(preselectedStall ?? null);
  const [locationType, setLocationType] = useState<LocationType>('STALL');
  const [locationLabel, setLocationLabel] = useState('');
  const [chiefComplaint, setChiefComplaint] = useState('');
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    setSelectedStall(preselectedStall ?? null);
    if (preselectedStall) setLocationType(preselectedStall.type);
  }, [preselectedStall]);

  const { data: horses = [], isLoading: horsesLoading } = useQuery<Horse[]>(
    ['horses'],
    () => horseApi.list(),
    { enabled: open },
  );

  /**
   * ⚠ THIS USED TO READ `GET /users`, WHICH REQUIRES `manage:users` (users.controller.ts :169-170).
   *
   * Only owners and admins hold that slug, so for every veterinarian and technician — exactly the
   * people who admit patients — the read 403'd, `users` stayed `[]`, and the "Attending
   * Veterinarian *" picker below was permanently empty with no error shown. `handleSubmit`
   * requires a vet, so Admit was unreachable: not a crash and not a spinner (the global
   * `retry: 1` resolves after two attempts and `usersLoading` goes false), just a dead end that
   * says "Horse and attending vet are required" over an empty list.
   *
   * `GET /users/veterinarians` (users.controller.ts :176-180) is the read this dialog actually
   * wanted: no permission decorator, clinic-scoped, and already pre-filtered to the active
   * VETERINARIAN + OWNER set — so a sole proprietor who is their own vet now appears here, as
   * they already do in the Schedule's provider picker. It is the same endpoint
   * `pages/Appointments/index.tsx` uses for its vet list.
   */
  const { data: vets = [], isLoading: usersLoading } = useQuery<UserOption[]>(
    ['users', 'veterinarians'],
    () => api.get('/users/veterinarians').then((r) => r.data),
    { enabled: open },
  );

  const { data: stalls = [] } = useQuery<Stall[]>(
    ['stalls'],
    () => treatmentSheetApi.listStalls(),
    { enabled: open },
  );

  const locationsOfType = stalls.filter((s) => s.type === locationType);

  const createMutation = useMutation(
    (payload: CreateSheetPayload) => treatmentSheetApi.createSheet(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['stalls', 'occupancy']);
        onSuccess();
        handleClose();
      },
      onError: () => setError('Failed to admit patient. Please try again.'),
    },
  );

  const handleClose = () => {
    setSelectedHorse(null);
    setSelectedVet(null);
    setSelectedStall(null);
    setLocationType('STALL');
    setLocationLabel('');
    setChiefComplaint('');
    setError(null);
    onClose();
  };

  const handleSubmit = () => {
    if (!selectedHorse || !selectedVet) {
      setError('Horse and attending vet are required.');
      return;
    }
    const payload: CreateSheetPayload = {
      horseId: selectedHorse.id,
      attendingVetId: selectedVet.id,
      locationType,
      // A managed location can be picked for any type now (stall, field, or other).
      stallId: selectedStall ? selectedStall.id : undefined,
      locationLabel:
        (locationType === 'FIELD' || locationType === 'OTHER') ? (locationLabel || undefined) : undefined,
      chiefComplaint: chiefComplaint || undefined,
    };
    createMutation.mutate(payload);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Admit Patient</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* Horse selector */}
          <Autocomplete<Horse>
            options={horses}
            loading={horsesLoading}
            getOptionLabel={patientLabel}
            // Two horses can share a name (e.g. "Zorro") — key the dropdown by id,
            // not the label, so React keys stay unique, and compare by id.
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {patientLabel(option)}
              </li>
            )}
            value={selectedHorse}
            onChange={(_, v) => setSelectedHorse(v)}
            renderInput={(params) => (
              <TextField {...params} label="Horse *" placeholder="Search by name or patient ID..." />
            )}
          />

          {/* Vet selector */}
          <Autocomplete<UserOption>
            options={vets}
            loading={usersLoading}
            getOptionLabel={(o) => o.name}
            value={selectedVet}
            onChange={(_, v) => setSelectedVet(v)}
            renderInput={(params) => (
              <TextField {...params} label="Attending Veterinarian *" />
            )}
          />

          {/* Location type */}
          <Box>
            <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
              Location Type
            </Typography>
            <ToggleButtonGroup
              value={locationType}
              exclusive
              onChange={(_, v) => {
                if (!v) return;
                setLocationType(v as LocationType);
                // A location of the previous type no longer matches the picker.
                setSelectedStall(null);
              }}
              size="small"
            >
              <ToggleButton value="STALL">
                <StallIcon sx={{ mr: 0.5, fontSize: 16 }} />
                Stall
              </ToggleButton>
              <ToggleButton value="FIELD">
                <FieldIcon sx={{ mr: 0.5, fontSize: 16 }} />
                Field
              </ToggleButton>
              <ToggleButton value="OTHER">Other</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Managed location of the selected type */}
          <Autocomplete<Stall>
            options={locationsOfType}
            getOptionLabel={(o) => o.name + (o.section ? ` (${o.section})` : '')}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={selectedStall}
            onChange={(_, v) => setSelectedStall(v)}
            noOptionsText={`No ${LOCATION_TYPE_LABEL[locationType].toLowerCase()} locations configured — add one in "Manage Locations"`}
            renderInput={(params) => (
              <TextField
                {...params}
                label={LOCATION_TYPE_LABEL[locationType]}
                placeholder={`Select a ${LOCATION_TYPE_LABEL[locationType].toLowerCase()}...`}
              />
            )}
          />

          {/* Optional free-text label for one-off field/other locations */}
          {(locationType === 'FIELD' || locationType === 'OTHER') && (
            <TextField
              label={locationType === 'FIELD' ? 'Field / Pasture Label (optional)' : 'Location Label (optional)'}
              value={locationLabel}
              onChange={(e) => setLocationLabel(e.target.value)}
              placeholder={locationType === 'FIELD' ? 'e.g. North Pasture' : 'e.g. Trailer, Owner barn'}
            />
          )}

          {/* Chief complaint */}
          <TextField
            label="Chief Complaint"
            value={chiefComplaint}
            onChange={(e) => setChiefComplaint(e.target.value)}
            multiline
            minRows={2}
            placeholder="Reason for admission..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createMutation.isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={createMutation.isLoading}
          startIcon={createMutation.isLoading ? <CircularProgress size={16} /> : <HospitalIcon />}
        >
          Admit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Move Patient Dialog ──────────────────────────────────────────────────────

interface MoveDialogProps {
  open: boolean;
  occupancy: StallOccupancy | null;
  allOccupancy: StallOccupancy[];
  onClose: () => void;
}

const MoveDialog: React.FC<MoveDialogProps> = ({ open, occupancy, allOccupancy, onClose }) => {
  const queryClient = useQueryClient();
  const [targetStall, setTargetStall] = useState<Stall | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset when dialog opens
  React.useEffect(() => {
    if (open) { setTargetStall(null); setError(null); }
  }, [open]);

  // Available stalls: active STALL-type locations, empty, not the current stall.
  // (This dialog moves a patient into a stall and stamps locationType STALL, so
  // field/other managed locations are intentionally excluded here.)
  const availableStalls = allOccupancy
    .filter((o) => o.stall.isActive && o.stall.type === 'STALL' && !o.currentSheet && o.stall.id !== occupancy?.stall.id)
    .map((o) => o.stall);

  const moveMutation = useMutation(
    (stallId: string) =>
      treatmentSheetApi.updateSheet(occupancy!.currentSheet!.id, {
        stallId,
        locationType: 'STALL',
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['stalls', 'occupancy']);
        onClose();
      },
      onError: () => setError('Failed to move patient. Please try again.'),
    },
  );

  const handleSubmit = () => {
    if (!targetStall) { setError('Please select a destination stall.'); return; }
    moveMutation.mutate(targetStall.id);
  };

  const horse = occupancy?.horse;
  const currentStall = occupancy?.stall;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Move Patient</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* Patient summary */}
          <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={700}>{horse?.name ?? '—'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {horse?.breed ?? ''} · Currently in {currentStall?.name ?? '—'}
            </Typography>
          </Box>

          {/* Destination stall */}
          <Autocomplete<Stall>
            options={availableStalls}
            getOptionLabel={(o) => o.name + (o.section ? ` (${o.section})` : '')}
            value={targetStall}
            onChange={(_, v) => setTargetStall(v)}
            noOptionsText="No empty stalls available"
            renderInput={(params) => (
              <TextField {...params} label="Move to Stall *" placeholder="Select destination stall..." />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<MoveIcon />}
          disabled={!targetStall || moveMutation.isLoading}
          onClick={handleSubmit}
        >
          {moveMutation.isLoading ? 'Moving…' : 'Move'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Manage Locations Dialog ──────────────────────────────────────────────────

interface ManageLocationsDialogProps {
  open: boolean;
  onClose: () => void;
}

const ManageLocationsDialog: React.FC<ManageLocationsDialogProps> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<LocationType>('STALL');
  const [newSection, setNewSection] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const { data: locations = [], isLoading } = useQuery<Stall[]>(
    ['stalls'],
    () => treatmentSheetApi.listStalls(),
    { enabled: open },
  );

  const createMutation = useMutation(
    () => treatmentSheetApi.createStall({ name: newName, type: newType, section: newSection || undefined }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['stalls']);
        queryClient.invalidateQueries(['stalls', 'occupancy']);
        setNewName('');
        setNewSection('');
      },
    },
  );

  const deleteMutation = useMutation(
    (id: string) => treatmentSheetApi.deleteStall(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['stalls']);
        queryClient.invalidateQueries(['stalls', 'occupancy']);
        setConfirmDeleteId(null);
      },
    },
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Manage Locations</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle2">Existing Locations</Typography>
          {isLoading ? (
            <Skeleton height={40} />
          ) : locations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No locations configured yet.
            </Typography>
          ) : (
            locations.map((s) => (
              <Box key={s.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Chip label={LOCATION_TYPE_LABEL[s.type] ?? s.type} size="small" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                  <Typography variant="body2" noWrap>
                    {s.name}{s.section ? ` (${s.section})` : ''}
                  </Typography>
                </Box>
                {confirmDeleteId === s.id ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Typography variant="caption" color="error">Remove?</Typography>
                    <Button
                      size="small"
                      color="error"
                      variant="contained"
                      sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11 }}
                      disabled={deleteMutation.isLoading}
                      onClick={() => deleteMutation.mutate(s.id)}
                    >
                      Yes
                    </Button>
                    <Button
                      size="small"
                      sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11 }}
                      onClick={() => setConfirmDeleteId(null)}
                    >
                      No
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Chip label={s.isActive ? 'Active' : 'Inactive'} size="small" />
                    {s.isActive && (
                      <Tooltip title="Deactivate location">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setConfirmDeleteId(s.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
            ))
          )}

          <Box sx={{ mt: 1, borderTop: 1, borderColor: 'divider', pt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Add Location
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <ToggleButtonGroup
                value={newType}
                exclusive
                onChange={(_, v) => v && setNewType(v as LocationType)}
                size="small"
              >
                <ToggleButton value="STALL">
                  <StallIcon sx={{ mr: 0.5, fontSize: 16 }} />
                  Stall
                </ToggleButton>
                <ToggleButton value="FIELD">
                  <FieldIcon sx={{ mr: 0.5, fontSize: 16 }} />
                  Field
                </ToggleButton>
                <ToggleButton value="OTHER">Other</ToggleButton>
              </ToggleButtonGroup>
              <TextField
                label={`${LOCATION_TYPE_LABEL[newType]} Name *`}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                size="small"
                placeholder={newType === 'STALL' ? 'e.g. Stall 12' : newType === 'FIELD' ? 'e.g. North Pasture' : 'e.g. Trailer'}
              />
              <TextField
                label="Section (optional)"
                value={newSection}
                onChange={(e) => setNewSection(e.target.value)}
                size="small"
                placeholder="e.g. ICU Wing"
              />
              <Button
                variant="contained"
                size="small"
                disabled={!newName.trim() || createMutation.isLoading}
                onClick={() => createMutation.mutate()}
              >
                Add Location
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Hospital Board Page ─────────────────────────────────────────────────

const HospitalBoard: React.FC = () => {
  const theme = useTheme();

  // Every write on this board — admit, move, discharge, and the stall CRUD in the Manage
  // Locations dialog — declares the one slug `manage:medical_records` across the treatment-sheet
  // module's six controllers (INV-108). No `@Roles` anywhere in it.
  const rbac = useRBAC();
  const can = treatmentSheetControls(rbac);

  const [admitDialogOpen, setAdmitDialogOpen] = useState(false);
  const [manageLocationsOpen, setManageLocationsOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [preselectedStall, setPreselectedStall] = useState<Stall | null>(null);
  const [moveOccupancy, setMoveOccupancy] = useState<StallOccupancy | null>(null);

  const { data: occupancy = [], isLoading, isError, refetch } = useQuery<StallOccupancy[]>(
    ['stalls', 'occupancy'],
    () => treatmentSheetApi.getOccupancy(),
    { refetchInterval: 60000 },
  );

  // The stall grid shows STALL-type locations only; field/other managed locations
  // are pickable at admit time and field patients render in their own section below.
  const stallOccupancy = occupancy.filter((o) => o.stall.isActive && o.stall.type === 'STALL');
  const fieldSheets = occupancy.filter(
    (o) => o.currentSheet?.locationType === 'FIELD' && o.currentSheet?.status === 'ACTIVE',
  );

  const handleAdmitFromStall = (stall: Stall) => {
    setPreselectedStall(stall);
    setAdmitDialogOpen(true);
  };

  const handleAdmitOpen = () => {
    setPreselectedStall(null);
    setAdmitDialogOpen(true);
  };

  const handleMoveOpen = (occ: StallOccupancy) => {
    setMoveOccupancy(occ);
    setMoveDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <HospitalIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Hospital Board
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Real-time stall occupancy &amp; patient status
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {can.manageLocations && (
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => setManageLocationsOpen(true)}
            >
              Manage Locations
            </Button>
          )}
          {can.admit && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdmitOpen}>
              Admit Patient
            </Button>
          )}
        </Box>
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load occupancy data.{' '}
          <Button size="small" onClick={() => refetch()}>Retry</Button>
        </Alert>
      )}

      {/* Stall Grid */}
      <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <StallIcon fontSize="small" />
        Stalls
      </Typography>

      {isLoading ? (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      ) : stallOccupancy.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 4, textAlign: 'center', mb: 4, borderStyle: 'dashed' }}
        >
          <Typography color="text.secondary">
            No stalls configured.{' '}
            {can.manageLocations && (
              <Button size="small" onClick={() => setManageLocationsOpen(true)}>
                Add locations
              </Button>
            )}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {stallOccupancy.map((occ) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={occ.stall.id}>
              <StallCard
                occupancy={occ}
                onAdmit={can.admit ? handleAdmitFromStall : undefined}
                onMove={can.move ? handleMoveOpen : undefined}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Field Patients */}
      {fieldSheets.length > 0 && (
        <>
          <Typography
            variant="h6"
            fontWeight={600}
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <FieldIcon fontSize="small" />
            Field Patients
          </Typography>
          <Grid container spacing={2}>
            {fieldSheets.map((occ) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={occ.currentSheet?.id ?? occ.stall.id}>
                <StallCard
                occupancy={occ}
                onAdmit={can.admit ? handleAdmitFromStall : undefined}
                onMove={can.move ? handleMoveOpen : undefined}
              />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Dialogs */}
      <AdmitDialog
        open={admitDialogOpen}
        preselectedStall={preselectedStall}
        onClose={() => setAdmitDialogOpen(false)}
        onSuccess={() => {}}
      />
      <MoveDialog
        open={moveDialogOpen}
        occupancy={moveOccupancy}
        allOccupancy={occupancy}
        onClose={() => setMoveDialogOpen(false)}
      />
      <ManageLocationsDialog
        open={manageLocationsOpen}
        onClose={() => setManageLocationsOpen(false)}
      />
    </Box>
  );
};

export default HospitalBoard;
